import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import {
  TUTORIAL_AUTO_ADVANCE_SECONDS,
  TUTORIAL_DEFAULT_AUDIO_HINT_TEXT,
  TUTORIAL_MUSIC_BY_FLOW,
} from './constants';
import { createAmbientTrack, createTutorialAmbient } from './tutorialAudio';
import {
  readTutorialSeen,
  readUnitModalTutorialRepeatEnabled,
  saveTutorialSeen,
  saveUnitModalTutorialRepeatEnabled,
} from './tutorialStorage';
import dashboardTutorialStepsSource from './tutorialFlows/dashboardTutorial';
import logisticsTutorialStepsSource from './tutorialFlows/logisticsTutorial';
import routeCreatorTutorialStepsSource from './tutorialFlows/routeCreatorTutorial';
import serviceTutorialStepsSource from './tutorialFlows/serviceTutorial';
import getMonitorTutorialSteps from './tutorialFlows/monitorTutorial';
import reportsTutorialStepsSource from './tutorialFlows/reportsTutorial';
import fleetExpensesTutorialStepsSource from './tutorialFlows/fleetExpensesTutorial';
import stockTutorialStepsSource from './tutorialFlows/stockTutorial';
import unitModalTutorialStepsSource from './tutorialFlows/unitModalTutorial';

const ELEVENLABS_DEFAULT_VOICE_ID = 'JBFqnCBsd6RMkjVDRZzb';
const ELEVENLABS_DEFAULT_MODEL_ID = 'eleven_multilingual_v2';

const useTutorial = ({ view, setView, selectedTransport, scopeKey = 'global' }) => {
  const [currentTutorialId, setCurrentTutorialId] = useState('dashboard');
  const [tutorialRun, setTutorialRun] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [tutorialStepCountdown, setTutorialStepCountdown] = useState(TUTORIAL_AUTO_ADVANCE_SECONDS);
  const [tutorialAutoAdvancePaused, setTutorialAutoAdvancePaused] = useState(false);
  const [tutorialReadyToAdvance, setTutorialReadyToAdvance] = useState(false);
  const [queuedTutorialId, setQueuedTutorialId] = useState(null);
  const [tutorialShortcutOpen, setTutorialShortcutOpen] = useState(false);
  const [tutorialShortcutDock, setTutorialShortcutDock] = useState('top-right');
  const [tutorialSeen, setTutorialSeen] = useState(() => readTutorialSeen('dashboard', scopeKey));
  const [logisticsTutorialSeen, setLogisticsTutorialSeen] = useState(() => readTutorialSeen('logistics', scopeKey));
  const [routeCreatorTutorialSeen, setRouteCreatorTutorialSeen] = useState(() => readTutorialSeen('routeCreator', scopeKey));
  const [serviceTutorialSeen, setServiceTutorialSeen] = useState(() => readTutorialSeen('service', scopeKey));
  const [monitorTutorialSeen, setMonitorTutorialSeen] = useState(() => readTutorialSeen('monitor', scopeKey));
  const [reportsTutorialSeen, setReportsTutorialSeen] = useState(() => readTutorialSeen('reports', scopeKey));
  const [fleetExpensesTutorialSeen, setFleetExpensesTutorialSeen] = useState(() => readTutorialSeen('fleetExpenses', scopeKey));
  const [stockTutorialSeen, setStockTutorialSeen] = useState(() => readTutorialSeen('stock', scopeKey));
  const [monitorTutorialHasActiveRoutes, setMonitorTutorialHasActiveRoutes] = useState(true);
  const [unitModalTutorialSeen, setUnitModalTutorialSeen] = useState(() => readTutorialSeen('unitModal', scopeKey));
  const [unitModalTutorialRepeatEnabled, setUnitModalTutorialRepeatEnabled] = useState(() => readUnitModalTutorialRepeatEnabled());
  const [tutorialAudioHintVisible, setTutorialAudioHintVisible] = useState(false);
  const [tutorialAudioHintText, setTutorialAudioHintText] = useState(TUTORIAL_DEFAULT_AUDIO_HINT_TEXT);
  const [tutorialSessionKey, setTutorialSessionKey] = useState(0);

  const tutorialUtteranceRef = useRef(null);
  const tutorialAudioRef = useRef(null);
  const tutorialAmbientRef = useRef(null);
  const tutorialAudioCacheRef = useRef(new Map());
  const tutorialPlaybackIdRef = useRef(0);
  const tutorialAudioHintTimeoutRef = useRef(null);
  const tutorialShortcutPanelRef = useRef(null);
  const tutorialCountdownIntervalRef = useRef(null);
  const tutorialAutoAdvancePausedRef = useRef(false);
  const queuedTutorialStartRef = useRef(null);
  const tutorialStartTimeoutRef = useRef(null);
  const manualTutorialJumpRef = useRef(null);
  const ignoreTutorialCallbackRef = useRef(false);
  const manualTutorialNavigationRef = useRef(0);

  const elevenLabsApiKey = process.env.REACT_APP_ELEVENLABS_API_KEY || '';
  const elevenLabsVoiceId = process.env.REACT_APP_ELEVENLABS_VOICE_ID || ELEVENLABS_DEFAULT_VOICE_ID;
  const elevenLabsModelId = process.env.REACT_APP_ELEVENLABS_MODEL_ID || ELEVENLABS_DEFAULT_MODEL_ID;

  const dashboardTutorialSteps = useMemo(() => dashboardTutorialStepsSource, []);
  const logisticsTutorialSteps = useMemo(() => logisticsTutorialStepsSource, []);
  const routeCreatorTutorialSteps = useMemo(() => routeCreatorTutorialStepsSource, []);
  const serviceTutorialSteps = useMemo(() => serviceTutorialStepsSource, []);
  const monitorTutorialSteps = useMemo(
    () => getMonitorTutorialSteps({ hasActiveRoutes: monitorTutorialHasActiveRoutes }),
    [monitorTutorialHasActiveRoutes]
  );
  const reportsTutorialSteps = useMemo(() => reportsTutorialStepsSource, []);
  const fleetExpensesTutorialSteps = useMemo(() => fleetExpensesTutorialStepsSource, []);
  const stockTutorialSteps = useMemo(() => stockTutorialStepsSource, []);
  const unitModalTutorialSteps = useMemo(() => unitModalTutorialStepsSource, []);

  useEffect(() => {
    setTutorialSeen(readTutorialSeen('dashboard', scopeKey));
    setLogisticsTutorialSeen(readTutorialSeen('logistics', scopeKey));
    setRouteCreatorTutorialSeen(readTutorialSeen('routeCreator', scopeKey));
    setServiceTutorialSeen(readTutorialSeen('service', scopeKey));
    setMonitorTutorialSeen(readTutorialSeen('monitor', scopeKey));
    setReportsTutorialSeen(readTutorialSeen('reports', scopeKey));
    setFleetExpensesTutorialSeen(readTutorialSeen('fleetExpenses', scopeKey));
    setStockTutorialSeen(readTutorialSeen('stock', scopeKey));
    setUnitModalTutorialSeen(readTutorialSeen('unitModal', scopeKey));
  }, [scopeKey]);

  const currentTutorialSteps = useMemo(() => {
    const baseSteps =
      currentTutorialId === 'logistics'
        ? logisticsTutorialSteps
        : currentTutorialId === 'service'
          ? serviceTutorialSteps
        : currentTutorialId === 'monitor'
          ? monitorTutorialSteps
          : currentTutorialId === 'reports'
            ? reportsTutorialSteps
          : currentTutorialId === 'fleetExpenses'
            ? fleetExpensesTutorialSteps
          : currentTutorialId === 'stock'
            ? stockTutorialSteps
          : currentTutorialId === 'routeCreator'
            ? routeCreatorTutorialSteps
            : currentTutorialId === 'unitModal'
              ? unitModalTutorialSteps
              : dashboardTutorialSteps;

    return baseSteps.map((step) => ({
      ...step,
      content: step.narration || step.content,
    }));
  }, [currentTutorialId, dashboardTutorialSteps, logisticsTutorialSteps, serviceTutorialSteps, monitorTutorialSteps, reportsTutorialSteps, fleetExpensesTutorialSteps, stockTutorialSteps, routeCreatorTutorialSteps, unitModalTutorialSteps]);

  const safeJoyrideSteps = tutorialRun ? currentTutorialSteps : [];
  const currentTutorialTarget = tutorialRun ? currentTutorialSteps[tutorialStepIndex]?.target || null : null;

  const scrollToTutorialTarget = useCallback((targetSelector, options = {}) => {
    if (typeof window === 'undefined' || !targetSelector) return false;

    const { attempts = 14, delay = 120, block = 'center', behavior = 'smooth' } = options;
    let tries = 0;

    const findScrollableParent = (node) => {
      if (!node || typeof window === 'undefined') return null;

      let parent = node.parentElement;
      while (parent && parent !== document.body) {
        const styles = window.getComputedStyle(parent);
        const overflowY = styles.overflowY;
        const isScrollable = /(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight + 8;
        if (isScrollable) {
          return parent;
        }
        parent = parent.parentElement;
      }

      return null;
    };

    const centerNodeInContainer = (container, node) => {
      if (!container || !node) return;
      const containerRect = container.getBoundingClientRect();
      const nodeRect = node.getBoundingClientRect();
      const targetTop = container.scrollTop + (nodeRect.top - containerRect.top) - (container.clientHeight / 2) + (nodeRect.height / 2);
      container.scrollTo({ top: Math.max(0, targetTop), behavior });
    };

    const centerNodeInViewport = (node) => {
      if (!node) return;
      const nodeRect = node.getBoundingClientRect();
      const absoluteTop = window.scrollY + nodeRect.top;
      const targetTop = absoluteTop - (window.innerHeight / 2) + (nodeRect.height / 2);
      window.scrollTo({ top: Math.max(0, targetTop - 24), behavior });
    };

    const run = () => {
      const targetNode = document.querySelector(targetSelector);
      if (targetNode) {
        const scrollableParent = findScrollableParent(targetNode);

        if (scrollableParent) {
          centerNodeInContainer(scrollableParent, targetNode);
        }

        targetNode.scrollIntoView({ behavior, block, inline: 'nearest' });
        window.setTimeout(() => {
          centerNodeInViewport(targetNode);
        }, behavior === 'smooth' ? 120 : 0);

        if (typeof targetNode.focus === 'function') {
          const hadTabIndex = targetNode.hasAttribute('tabindex');
          if (!hadTabIndex) {
            targetNode.setAttribute('tabindex', '-1');
          }
          targetNode.focus({ preventScroll: true });
          if (!hadTabIndex) {
            window.setTimeout(() => targetNode.removeAttribute('tabindex'), 600);
          }
        }

        return true;
      }

      tries += 1;
      if (tries < attempts) {
        window.setTimeout(run, delay);
      }

      return false;
    };

    return run();
  }, []);

  useEffect(() => {
    if (!tutorialRun || !tutorialShortcutOpen || typeof window === 'undefined') return undefined;

    if (Date.now() - manualTutorialNavigationRef.current < 1400) {
      return undefined;
    }

    const panelNode = tutorialShortcutPanelRef.current;
    if (!panelNode) return undefined;

    const candidates = ['top-right', 'top-left', 'bottom-right', 'bottom-left'];
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const panelRect = panelNode.getBoundingClientRect();
    const panelWidth = panelRect.width || 288;
    const panelHeight = panelRect.height || 420;
    const margin = 20;

    const candidateRects = {
      'top-right': { left: viewportWidth - panelWidth - margin, top: 96, right: viewportWidth - margin, bottom: 96 + panelHeight },
      'top-left': { left: margin, top: 96, right: margin + panelWidth, bottom: 96 + panelHeight },
      'bottom-right': {
        left: viewportWidth - panelWidth - margin,
        top: Math.max(margin, viewportHeight - panelHeight - margin),
        right: viewportWidth - margin,
        bottom: viewportHeight - margin,
      },
      'bottom-left': {
        left: margin,
        top: Math.max(margin, viewportHeight - panelHeight - margin),
        right: margin + panelWidth,
        bottom: viewportHeight - margin,
      },
    };

    const targetNode = currentTutorialTarget ? document.querySelector(currentTutorialTarget) : null;
    if (!targetNode) {
      setTutorialShortcutDock('top-right');
      return undefined;
    }

    const targetRect = targetNode.getBoundingClientRect();
    const expandedTargetRect = {
      left: targetRect.left - 24,
      top: targetRect.top - 24,
      right: targetRect.right + 24,
      bottom: targetRect.bottom + 24,
    };

    const getOverlapArea = (rectA, rectB) => {
      const overlapWidth = Math.max(0, Math.min(rectA.right, rectB.right) - Math.max(rectA.left, rectB.left));
      const overlapHeight = Math.max(0, Math.min(rectA.bottom, rectB.bottom) - Math.max(rectA.top, rectB.top));
      return overlapWidth * overlapHeight;
    };

    const bestCandidate = candidates
      .map((candidate) => ({
        candidate,
        overlap: getOverlapArea(candidateRects[candidate], expandedTargetRect),
      }))
      .sort((a, b) => a.overlap - b.overlap)[0];

    setTutorialShortcutDock(bestCandidate?.candidate || 'top-right');
    return undefined;
  }, [currentTutorialTarget, tutorialRun, tutorialShortcutOpen, tutorialStepIndex]);

  useEffect(() => {
    if (!tutorialRun || !currentTutorialTarget || queuedTutorialId) return undefined;
    const timeoutId = window.setTimeout(() => {
      scrollToTutorialTarget(currentTutorialTarget, { attempts: 10, delay: 120, block: 'center' });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [currentTutorialTarget, queuedTutorialId, scrollToTutorialTarget, tutorialRun, tutorialStepIndex]);

  const clearTutorialCountdown = useCallback(() => {
    if (tutorialCountdownIntervalRef.current) {
      window.clearInterval(tutorialCountdownIntervalRef.current);
      tutorialCountdownIntervalRef.current = null;
    }
  }, []);

  const clearTutorialStartTimeout = useCallback(() => {
    if (tutorialStartTimeoutRef.current) {
      window.clearTimeout(tutorialStartTimeoutRef.current);
      tutorialStartTimeoutRef.current = null;
    }
  }, []);

  const finishTutorialRun = useCallback(() => {
    clearTutorialCountdown();
    clearTutorialStartTimeout();
    manualTutorialJumpRef.current = null;
    ignoreTutorialCallbackRef.current = false;
    queuedTutorialStartRef.current = null;
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialAutoAdvancePaused(false);
    setTutorialRun(false);
    setTutorialStepIndex(0);
    setTutorialAudioHintVisible(false);
    setTutorialShortcutOpen(false);
    setTutorialSessionKey((current) => current + 1);
  }, [clearTutorialCountdown, clearTutorialStartTimeout]);

  const stopTutorialAmbient = useCallback(() => {
    if (tutorialAmbientRef.current) {
      tutorialAmbientRef.current.stop();
      tutorialAmbientRef.current = null;
    }
  }, []);

  const startTutorialAmbient = useCallback((forceRestart = false, flowId = currentTutorialId) => {
    if (tutorialAmbientRef.current && !forceRestart) {
      tutorialAmbientRef.current.resume?.();
      return;
    }

    stopTutorialAmbient();
    const ambientTrack = TUTORIAL_MUSIC_BY_FLOW[flowId] || TUTORIAL_MUSIC_BY_FLOW.dashboard;
    const ambient =
      createAmbientTrack(ambientTrack, flowId === 'logistics' || flowId === 'routeCreator' || flowId === 'monitor' ? 0.24 : 0.22)
      || createTutorialAmbient();
    tutorialAmbientRef.current = ambient;
    ambient?.resume?.();
  }, [currentTutorialId, stopTutorialAmbient]);

  const stopTutorialSpeech = useCallback(() => {
    clearTutorialStartTimeout();
    tutorialPlaybackIdRef.current += 1;
    if (tutorialAudioRef.current) {
      tutorialAudioRef.current.pause();
      tutorialAudioRef.current.currentTime = 0;
      tutorialAudioRef.current = null;
    }
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    tutorialUtteranceRef.current = null;
  }, [clearTutorialStartTimeout]);

  const speakWithBrowserVoice = useCallback((text, playbackId = tutorialPlaybackIdRef.current) => {
    if (typeof window === 'undefined' || !window.speechSynthesis || !text) return Promise.resolve(false);
    if (tutorialPlaybackIdRef.current !== playbackId) return Promise.resolve(false);

    window.speechSynthesis.cancel();

    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'es-AR';
      utterance.rate = 0.97;
      utterance.pitch = 1.08;
      utterance.volume = 1;

      const voices = window.speechSynthesis.getVoices?.() || [];
      const preferredVoice =
        voices.find((voice) => /es(-|_)(ar|es|mx|us)/i.test(voice.lang) && /female|helena|laura|sofia|paulina|monica|maria|sabina/i.test(voice.name))
        || voices.find((voice) => /es(-|_)(ar|es|mx|us)/i.test(voice.lang))
        || null;

      if (preferredVoice) utterance.voice = preferredVoice;

      utterance.onend = () => resolve(true);
      utterance.onerror = () => resolve(false);

      tutorialUtteranceRef.current = utterance;
      if (tutorialPlaybackIdRef.current !== playbackId) {
        resolve(false);
        return;
      }

      window.speechSynthesis.speak(utterance);
    });
  }, []);

  const playElevenLabsNarration = useCallback(async (text, playbackId = tutorialPlaybackIdRef.current) => {
    if (!text || !elevenLabsApiKey) return false;
    if (tutorialPlaybackIdRef.current !== playbackId) return false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3500);

    try {
      const cachedUrl = tutorialAudioCacheRef.current.get(text);
      if (cachedUrl) {
        const audio = new Audio(cachedUrl);
        if (tutorialPlaybackIdRef.current !== playbackId) return false;
        tutorialAudioRef.current = audio;
        return await new Promise((resolve, reject) => {
          audio.onended = () => resolve(true);
          audio.onerror = () => reject(new Error('No se pudo reproducir el audio cacheado del tutorial.'));
          audio.play().catch(reject);
        });
      }

      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${elevenLabsVoiceId}/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'xi-api-key': elevenLabsApiKey,
        },
        signal: controller.signal,
        body: JSON.stringify({
          text,
          model_id: elevenLabsModelId,
          language_code: 'es',
          output_format: 'mp3_44100_128',
          voice_settings: {
            stability: 0.45,
            similarity_boost: 0.78,
            style: 0.22,
            use_speaker_boost: true,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`ElevenLabs respondió ${response.status}`);
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      tutorialAudioCacheRef.current.set(text, audioUrl);

      const audio = new Audio(audioUrl);
      if (tutorialPlaybackIdRef.current !== playbackId) return false;
      tutorialAudioRef.current = audio;
      return await new Promise((resolve, reject) => {
        audio.onended = () => resolve(true);
        audio.onerror = () => reject(new Error('No se pudo reproducir el audio generado por ElevenLabs.'));
        audio.play().catch(reject);
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }, [elevenLabsApiKey, elevenLabsModelId, elevenLabsVoiceId]);

  const playTutorialAudioFile = useCallback(async (audioPath, playbackId = tutorialPlaybackIdRef.current) => {
    if (!audioPath) return false;
    if (tutorialPlaybackIdRef.current !== playbackId) return false;

    const audio = new Audio(audioPath);
    if (tutorialPlaybackIdRef.current !== playbackId) return false;
    tutorialAudioRef.current = audio;

    return await new Promise((resolve, reject) => {
      audio.onended = () => resolve(true);
      audio.onerror = () => reject(new Error(`No se pudo reproducir ${audioPath}`));
      audio.play().catch(reject);
    });
  }, []);

  const speakTutorialStep = useCallback((step) => {
    if (!step?.narration) {
      setTutorialReadyToAdvance(true);
      setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
      return;
    }

    stopTutorialSpeech();
    clearTutorialCountdown();
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    const playbackId = tutorialPlaybackIdRef.current;
    const playStepAudio = step.audio
      ? playTutorialAudioFile(step.audio, playbackId)
      : Promise.reject(new Error('No local tutorial audio'));

    playStepAudio
      .catch(() => playElevenLabsNarration(step.narration, playbackId))
      .catch(() => {
        if (tutorialPlaybackIdRef.current !== playbackId) return;
        return speakWithBrowserVoice(step.narration, playbackId);
      })
      .finally(() => {
        if (tutorialPlaybackIdRef.current !== playbackId) return;
        setTutorialReadyToAdvance(true);
        setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
      });
  }, [clearTutorialCountdown, playElevenLabsNarration, playTutorialAudioFile, speakWithBrowserVoice, stopTutorialSpeech]);

  const queueTutorialSpeechStart = useCallback((step, delay = 220) => {
    clearTutorialStartTimeout();
    tutorialStartTimeoutRef.current = window.setTimeout(() => {
      tutorialStartTimeoutRef.current = null;
      speakTutorialStep(step);
    }, delay);
  }, [clearTutorialStartTimeout, speakTutorialStep]);

  const getTutorialContinueTarget = useCallback(() => {
    if (currentTutorialId === 'dashboard') return 'logistics';
    if (currentTutorialId === 'logistics') return 'routeCreator';
    return null;
  }, [currentTutorialId]);

  const advanceTutorialStep = useCallback(() => {
    if (!tutorialRun) return;

    const isLastStep = tutorialStepIndex >= currentTutorialSteps.length - 1;
    if (isLastStep) {
      const continueTarget = getTutorialContinueTarget();
      stopTutorialSpeech();
      finishTutorialRun();
      if (continueTarget === 'logistics') {
        setView('logistics');
        setQueuedTutorialId('logistics');
      } else if (continueTarget === 'routeCreator') {
        setView('logistics');
        setQueuedTutorialId('routeCreator');
      } else {
        setQueuedTutorialId(null);
        stopTutorialAmbient();
      }
      return;
    }

    const nextIndex = tutorialStepIndex + 1;
    const nextStep = currentTutorialSteps[nextIndex];
    setTutorialStepIndex(nextIndex);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    if (nextStep?.target) {
      scrollToTutorialTarget(nextStep.target, { attempts: 10, delay: 120, block: 'center' });
    }
    speakTutorialStep(nextStep);
  }, [currentTutorialSteps, finishTutorialRun, getTutorialContinueTarget, scrollToTutorialTarget, speakTutorialStep, stopTutorialAmbient, stopTutorialSpeech, tutorialRun, tutorialStepIndex, setView]);

  const showTutorialAudioHint = useCallback((text) => {
    setTutorialAudioHintText(text || TUTORIAL_DEFAULT_AUDIO_HINT_TEXT);
    setTutorialAudioHintVisible(true);

    if (tutorialAudioHintTimeoutRef.current) {
      window.clearTimeout(tutorialAudioHintTimeoutRef.current);
    }

    tutorialAudioHintTimeoutRef.current = window.setTimeout(() => {
      setTutorialAudioHintVisible(false);
    }, 3400);
  }, []);

  useEffect(() => {
    tutorialAutoAdvancePausedRef.current = tutorialAutoAdvancePaused;
  }, [tutorialAutoAdvancePaused]);

  useEffect(() => {
    clearTutorialCountdown();

    if (!tutorialRun || !tutorialReadyToAdvance || tutorialAutoAdvancePaused) {
      return undefined;
    }

    tutorialCountdownIntervalRef.current = window.setInterval(() => {
      setTutorialStepCountdown((current) => {
        if (current <= 1) {
          clearTutorialCountdown();
          window.setTimeout(() => advanceTutorialStep(), 0);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => {
      clearTutorialCountdown();
    };
  }, [advanceTutorialStep, clearTutorialCountdown, tutorialAutoAdvancePaused, tutorialReadyToAdvance, tutorialRun, tutorialStepIndex]);

  const startDashboardTutorial = useCallback(() => {
    saveTutorialSeen('dashboard', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('dashboard');
    setTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'dashboard');
    showTutorialAudioHint('Subí un poco el volumen: este tutorial de inicio se acompaña con voz.');
    queueTutorialSpeechStart(dashboardTutorialSteps[0], 220);
  }, [dashboardTutorialSteps, queueTutorialSpeechStart, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startLogisticsTutorial = useCallback(() => {
    saveTutorialSeen('logistics', true, scopeKey);
    stopTutorialSpeech();
    setQueuedTutorialId(null);
    setCurrentTutorialId('logistics');
    setLogisticsTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'logistics');
    showTutorialAudioHint('Vas a escuchar una guía breve mientras recorremos el módulo de Logística.');
    queueTutorialSpeechStart(logisticsTutorialSteps[0], 320);
  }, [logisticsTutorialSteps, queueTutorialSpeechStart, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startRouteCreatorTutorial = useCallback(() => {
    saveTutorialSeen('routeCreator', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('routeCreator');
    setRouteCreatorTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'routeCreator');
    showTutorialAudioHint('Este recorrido de hoja de ruta también tiene guía por voz.');
    queueTutorialSpeechStart(routeCreatorTutorialSteps[0], 220);
  }, [queueTutorialSpeechStart, routeCreatorTutorialSteps, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startServiceTutorial = useCallback(() => {
    saveTutorialSeen('service', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('service');
    setServiceTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'service');
    showTutorialAudioHint('Este recorrido te ubica rápido dentro del módulo de Servicio.');
    queueTutorialSpeechStart(serviceTutorialSteps[0], 220);
  }, [queueTutorialSpeechStart, scopeKey, serviceTutorialSteps, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startMonitorTutorial = useCallback(() => {
    saveTutorialSeen('monitor', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('monitor');
    setMonitorTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'monitor');
    showTutorialAudioHint('Este recorrido te acompaña con voz para leer el monitor global.');
    queueTutorialSpeechStart(monitorTutorialSteps[0], 220);
  }, [monitorTutorialSteps, queueTutorialSpeechStart, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startReportsTutorial = useCallback(() => {
    saveTutorialSeen('reports', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('reports');
    setReportsTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'reports');
    showTutorialAudioHint('Este recorrido te acompaña con voz para leer el módulo de Reportes.');
    queueTutorialSpeechStart(reportsTutorialSteps[0], 220);
  }, [queueTutorialSpeechStart, reportsTutorialSteps, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startFleetExpensesTutorial = useCallback(() => {
    saveTutorialSeen('fleetExpenses', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('fleetExpenses');
    setFleetExpensesTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'fleetExpenses');
    showTutorialAudioHint('Este recorrido te acompaña con voz oficial para leer Gastos de Flota.');
    queueTutorialSpeechStart(fleetExpensesTutorialSteps[0], 220);
  }, [fleetExpensesTutorialSteps, queueTutorialSpeechStart, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech]);

  const startStockTutorial = useCallback(() => {
    saveTutorialSeen('stock', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('stock');
    setStockTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'stock');
    showTutorialAudioHint('Este recorrido te acompaña con voz oficial para leer Stock y movimientos.');
    queueTutorialSpeechStart(stockTutorialSteps[0], 220);
  }, [queueTutorialSpeechStart, scopeKey, showTutorialAudioHint, startTutorialAmbient, stockTutorialSteps, stopTutorialSpeech]);

  const startUnitModalTutorial = useCallback(() => {
    saveTutorialSeen('unitModal', true, scopeKey);
    stopTutorialSpeech();
    setCurrentTutorialId('unitModal');
    setUnitModalTutorialSeen(true);
    setTutorialAutoAdvancePaused(false);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    setTutorialStepIndex(0);
    setTutorialSessionKey((current) => current + 1);
    setTutorialShortcutOpen(true);
    setTutorialRun(true);
    startTutorialAmbient(false, 'unitModal');
    showTutorialAudioHint('Esta guía te acompaña con voz dentro de la ficha de unidad.');
    queueTutorialSpeechStart(unitModalTutorialSteps[0], 220);
  }, [queueTutorialSpeechStart, scopeKey, showTutorialAudioHint, startTutorialAmbient, stopTutorialSpeech, unitModalTutorialSteps]);

  const jumpToTutorialStep = useCallback((targetIndex) => {
    const nextStep = currentTutorialSteps[targetIndex];
    if (!nextStep) return;
    manualTutorialNavigationRef.current = Date.now();
    clearTutorialCountdown();
    stopTutorialSpeech();
    manualTutorialJumpRef.current = { targetIndex };
    ignoreTutorialCallbackRef.current = true;
    setTutorialShortcutDock('top-right');
    setTutorialStepIndex(targetIndex);
    setTutorialReadyToAdvance(false);
    setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
    if (nextStep?.target) {
      scrollToTutorialTarget(nextStep.target, { attempts: 20, delay: 140, block: 'center', behavior: 'smooth' });
    }
    queueTutorialSpeechStart(nextStep, 120);
  }, [clearTutorialCountdown, currentTutorialSteps, queueTutorialSpeechStart, scrollToTutorialTarget, stopTutorialSpeech]);

  const jumpToLogisticsTutorial = useCallback(() => {
    stopTutorialSpeech();
    finishTutorialRun();
    setCurrentTutorialId('logistics');
    setView('logistics');
    setQueuedTutorialId('logistics');
    setTutorialShortcutOpen(false);
  }, [finishTutorialRun, setView, stopTutorialSpeech]);

  const queueRouteCreatorTutorial = useCallback(() => {
    setTutorialShortcutOpen(false);
    setQueuedTutorialId('routeCreator');
    setView('logistics');
  }, [setView]);

  const toggleUnitModalTutorialRepeat = useCallback(() => {
    setUnitModalTutorialRepeatEnabled((current) => {
      const nextValue = !current;
      saveUnitModalTutorialRepeatEnabled(nextValue);
      return nextValue;
    });
  }, []);

  const toggleTutorialAutoAdvancePaused = useCallback(() => {
    setTutorialAutoAdvancePaused((current) => !current);
  }, []);

  useEffect(() => {
    if (!tutorialRun) return;
    if (queuedTutorialId) return;
    setTutorialShortcutOpen(true);
  }, [queuedTutorialId, tutorialRun, currentTutorialId, tutorialStepIndex]);

  const handleTutorialCallback = useCallback((data) => {
    const { action, index, status, type } = data;
    const safeIndex = typeof index === 'number' ? index : tutorialStepIndex;
    const isLastStep = safeIndex >= currentTutorialSteps.length - 1;

    if (ignoreTutorialCallbackRef.current && (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND)) {
      ignoreTutorialCallbackRef.current = false;
      return;
    }

    if (manualTutorialJumpRef.current && type === EVENTS.STEP_BEFORE) {
      const { targetIndex } = manualTutorialJumpRef.current;
      if (safeIndex === targetIndex) {
        manualTutorialJumpRef.current = null;
      }
    }

    const closeTutorialFlow = (continueTarget = null) => {
      stopTutorialSpeech();
      finishTutorialRun();
      if (continueTarget) {
        setTutorialShortcutOpen(false);
        if (continueTarget === 'logistics') {
          setView('logistics');
        }
        if (continueTarget === 'routeCreator') {
          setView('logistics');
        }
        setQueuedTutorialId(continueTarget);
        return;
      }

      setQueuedTutorialId(null);
      stopTutorialAmbient();
    };

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE || type === EVENTS.TOUR_END) {
      const continueTarget = status === STATUS.FINISHED && action !== ACTIONS.CLOSE ? getTutorialContinueTarget() : null;
      closeTutorialFlow(continueTarget);
      return;
    }

    if (type === EVENTS.TOUR_START) {
      return;
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      if (isLastStep && action === ACTIONS.NEXT) {
        closeTutorialFlow(getTutorialContinueTarget());
        return;
      }

      const nextIndex = safeIndex + (action === ACTIONS.PREV ? -1 : 1);

      if (nextIndex >= 0 && nextIndex < currentTutorialSteps.length) {
        const nextStep = currentTutorialSteps[nextIndex];
        clearTutorialCountdown();
        setTutorialStepIndex(nextIndex);
        setTutorialReadyToAdvance(false);
        setTutorialStepCountdown(TUTORIAL_AUTO_ADVANCE_SECONDS);
        if (nextStep?.target) {
          scrollToTutorialTarget(nextStep.target, { attempts: 10, delay: 120, block: 'center' });
        }
        speakTutorialStep(nextStep);
      }
    }
  }, [clearTutorialCountdown, currentTutorialSteps, finishTutorialRun, getTutorialContinueTarget, scrollToTutorialTarget, setView, speakTutorialStep, stopTutorialAmbient, stopTutorialSpeech, tutorialStepIndex]);

  useEffect(() => {
    const audioCache = tutorialAudioCacheRef.current;
    return () => {
      stopTutorialSpeech();
      stopTutorialAmbient();
      clearTutorialStartTimeout();
      if (tutorialAudioHintTimeoutRef.current) {
        window.clearTimeout(tutorialAudioHintTimeoutRef.current);
      }
      audioCache.forEach((url) => URL.revokeObjectURL(url));
      audioCache.clear();
    };
  }, [clearTutorialStartTimeout, stopTutorialAmbient, stopTutorialSpeech]);

  useEffect(() => {
    if (!queuedTutorialId) return undefined;

    if ((queuedTutorialId === 'logistics' || queuedTutorialId === 'routeCreator') && view !== 'logistics') {
      setCurrentTutorialId(queuedTutorialId === 'routeCreator' ? 'routeCreator' : 'logistics');
      setTutorialRun(false);
      setView('logistics');
      return undefined;
    }

    if (queuedTutorialId === 'logistics' && view === 'logistics') {
      let attempts = 0;

      const tryStartTutorial = () => {
        const heroNode = document.querySelector('#logistics-hero');
        if (heroNode) {
          heroNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setQueuedTutorialId(null);
          queuedTutorialStartRef.current = null;
          window.setTimeout(() => {
            startLogisticsTutorial();
          }, 180);
          return;
        }

        attempts += 1;
        if (attempts >= 24) {
          setQueuedTutorialId(null);
          queuedTutorialStartRef.current = null;
          return;
        }

        queuedTutorialStartRef.current = window.setTimeout(tryStartTutorial, 180);
      };

      queuedTutorialStartRef.current = window.setTimeout(tryStartTutorial, 180);

      return () => {
        if (queuedTutorialStartRef.current) {
          window.clearTimeout(queuedTutorialStartRef.current);
          queuedTutorialStartRef.current = null;
        }
      };
    }

    if (queuedTutorialId === 'routeCreator' && view === 'logistics' && selectedTransport) {
      let attempts = 0;

      const tryStartRouteCreatorTutorial = () => {
        const heroNode = document.querySelector('#logistics-route-hero');
        if (heroNode) {
          heroNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setQueuedTutorialId(null);
          queuedTutorialStartRef.current = null;
          window.setTimeout(() => {
            startRouteCreatorTutorial();
          }, 180);
          return;
        }

        attempts += 1;
        if (attempts >= 24) {
          setQueuedTutorialId(null);
          queuedTutorialStartRef.current = null;
          return;
        }

        queuedTutorialStartRef.current = window.setTimeout(tryStartRouteCreatorTutorial, 180);
      };

      queuedTutorialStartRef.current = window.setTimeout(tryStartRouteCreatorTutorial, 180);

      return () => {
        if (queuedTutorialStartRef.current) {
          window.clearTimeout(queuedTutorialStartRef.current);
          queuedTutorialStartRef.current = null;
        }
      };
    }

    return undefined;
  }, [queuedTutorialId, selectedTransport, setView, startLogisticsTutorial, startRouteCreatorTutorial, view]);

  const overlayProps = useMemo(() => ({
    currentTutorialId,
    currentTutorialSteps,
    tutorialRun,
    tutorialStepIndex,
    tutorialReadyToAdvance,
    tutorialAutoAdvancePaused,
    tutorialStepCountdown,
    tutorialShortcutOpen,
    tutorialShortcutDock,
    tutorialShortcutPanelRef,
    tutorialSessionKey,
    safeJoyrideSteps,
    handleTutorialCallback,
    onToggleAutoAdvancePaused: toggleTutorialAutoAdvancePaused,
    onJumpToTutorialStep: jumpToTutorialStep,
    onJumpToLogisticsTutorial: jumpToLogisticsTutorial,
    onQueueRouteCreatorTutorial: queueRouteCreatorTutorial,
  }), [
    currentTutorialId,
    currentTutorialSteps,
    handleTutorialCallback,
    jumpToLogisticsTutorial,
    jumpToTutorialStep,
    queueRouteCreatorTutorial,
    safeJoyrideSteps,
    toggleTutorialAutoAdvancePaused,
    tutorialAutoAdvancePaused,
    tutorialReadyToAdvance,
    tutorialRun,
    tutorialShortcutDock,
    tutorialShortcutOpen,
    tutorialStepCountdown,
    tutorialStepIndex,
    tutorialSessionKey,
  ]);

  return {
    currentTutorialId,
    tutorialRun,
    currentTutorialTarget,
    queuedTutorialId,
    tutorialSeen,
    logisticsTutorialSeen,
    routeCreatorTutorialSeen,
    serviceTutorialSeen,
    monitorTutorialSeen,
    reportsTutorialSeen,
    fleetExpensesTutorialSeen,
    stockTutorialSeen,
    unitModalTutorialSeen,
    unitModalTutorialRepeatEnabled,
    tutorialAudioHintVisible,
    tutorialAudioHintText,
    tutorialSessionKey,
    startDashboardTutorial,
    startLogisticsTutorial,
    startRouteCreatorTutorial,
    startServiceTutorial,
    startMonitorTutorial,
    startReportsTutorial,
    startFleetExpensesTutorial,
    startStockTutorial,
    startUnitModalTutorial,
    toggleUnitModalTutorialRepeat,
    setMonitorTutorialHasActiveRoutes,
    overlayProps,
  };
};

export default useTutorial;
